import { RootStore } from '@vromo/dashboard/src/stores/RootStore';
import { useStore } from '@vromo/mobx-react/mobx-react';
import FontAwesome from '@vromo/ui/components/FontAwesome';
import Permissions from '@vromo/ui/components/Permissions';
import Tooltip from '@vromo/ui/components/Tooltip';
import { observer } from 'mobx-react-lite';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Route } from 'react-router';
import { Link } from 'react-router-dom';
import ActionBar from '../../../account/ActionBar/ActionBar';
import { UpgradeWrapper } from '../../../freemium/UpgradeModal';
import { getCSSStyles, linkContainerStyle, linkCSSStyles, SidebarList } from './sidebarMenu.style';

interface LinkProps {
    icon: string;
    path: string;
    label: string;
    isFreemium?: boolean;
}

const SideBarLink = ({ icon, path, label, isFreemium }: LinkProps) => {
    const renderLink = useCallback(
        () => (
            <Link to={path} data-sidebar-link={label} className={linkCSSStyles.container}>
                <FontAwesome weight="regular" icon={icon} x2 fixed />
                <span className={linkCSSStyles.label}>{label}</span>
            </Link>
        ),
        []
    );

    return (
        <Route
            path={path}
            exact
            children={({ match }) => {
                return (
                    <Tooltip position="right" label={label}>
                        <li className={linkContainerStyle(match)}>
                            {isFreemium ? (
                                <UpgradeWrapper>{renderLink()}</UpgradeWrapper>
                            ) : (
                                renderLink()
                            )}
                        </li>
                    </Tooltip>
                );
            }}
        />
    );
};

const SidebarMenu = observer(({ children }) => {
    const { t } = useTranslation();
    const [open, setOpen] = useState<boolean>(false);

    const styles = useMemo(() => getCSSStyles(open), [open]);
    const closeMenu = () => setOpen(false);

    const { authStore } = useStore<RootStore>('rootStore');
    const isEnterpriseUser = authStore.isEnterprise;

    return (
        <div className={styles.container}>
            <div onClick={closeMenu} className={styles.mobileTransparency} />
            <ActionBar toggleSidebar={() => setOpen(!open)} />
            <div className={styles.contentContainer}>
                <div className={styles.leftContent}>
                    <div onClick={closeMenu} className={styles.closeButton}>
                        &times;
                    </div>
                    <div className={styles.menuContainer}>
                        <SidebarList>
                            {!isEnterpriseUser && (
                                <>
                                    <Permissions minPosition="client">
                                        <SideBarLink
                                            label={t('menu.dashboard')}
                                            icon="th-large"
                                            path="/"
                                        />
                                    </Permissions>
                                    <Permissions minPosition="client">
                                        <SideBarLink
                                            label={t('menu.dispatch')}
                                            icon="map-marked-alt"
                                            path="/dispatch"
                                        />
                                    </Permissions>
                                    <Permissions minPosition="user">
                                        <SideBarLink
                                            label={t('menu.reports')}
                                            icon="analytics"
                                            path="/report"
                                        />
                                    </Permissions>
                                </>
                            )}
                            <Permissions minPosition="freemiumAdmin">
                                <>
                                    {!isEnterpriseUser && (
                                        <SideBarLink
                                            label={t('menu.plan')}
                                            icon="ruler-combined"
                                            path="/plan"
                                        />
                                    )}
                                    <SideBarLink
                                        label={t('menu.autoDispatch')}
                                        icon="project-diagram"
                                        path="/policies"
                                    />
                                    <SideBarLink
                                        label={t('menu.teams')}
                                        icon="users"
                                        path="/teams"
                                    />
                                    <SideBarLink
                                        label={t('menu.campaigns')}
                                        icon="bullhorn"
                                        path="/campaigns"
                                        isFreemium
                                    />
                                    <SideBarLink
                                        label={t('menu.sites')}
                                        icon="building"
                                        path="/sites"
                                    />
                                </>
                            </Permissions>
                        </SidebarList>
                    </div>
                </div>
                <div className={styles.childrenContainer}>
                    <Suspense fallback={<div />}>{children}</Suspense>
                </div>
            </div>
        </div>
    );
});

export default SidebarMenu;
